import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommandResult } from 'src/core/stores/types';
import {
  CreateRoleAssignmentItem,
  RoleAssignmentAttributes,
  RoleAssignmentItemStore,
  RoleAssignmentSummaryStore,
} from 'src/stores/role-assignments-store';
import { container } from 'src/inversify.config';
import { Alert, Checkbox, Modal, Spin, Form, Divider, App } from 'antd';
import { nameof } from 'src/core/utils/object';

import RolesSelect from 'src/common/components/business/roles-select';
import ScopeHierarchySelector from 'src/common/components/business/scope-hierarchy-selector';
import { IdentityType, ScopeType, Selector } from 'src/common/types';
import NewItemModal from 'src/core/ui/newitemmodal';
import { getSelectorFromScope } from 'src/common/utils';
import {
  GlobalLicenseManagerRoleName,
  LicenseApproverRoleName,
  LicenseManagerRoleName,
  RoleSummaryStore,
  StructmasterUserRoleName,
} from 'src/stores/roles-store';

import { Query } from 'src/core/stores/data-store';
import RoleAssignmentAttributesComponent from 'src/pages/role-assignments/role-attributes/role-attributes';
import { Typography } from 'antd';
import { GroupType } from 'src/stores/groups-store';
import {
  getUnlimitedLicenseAttributes,
  LicenseCreationAttributesStore,
  LicenseManagerAssignmentAttributes,
  NewLicensCreationAttributes,
} from 'src/stores/licensing-store';
import LicenseAttributesComponent from 'src/pages/licenses/license-attributes/license-attributes';
import moment from 'moment';

const { Text } = Typography;
interface GroupRoleAssignmentFormData {
  roles: { value: string; label: string }[];
  hierarchy: { scopeId: string; scopeTitle: string; scopeType: ScopeType; parentId: string; path: string[] };
}

const GroupRoleAssignmentModal: FC<{
  groupId: string;
  groupName: string;
  organizationName: string;
  groupOrganization?: string;
  onClose: (response?: CommandResult<CreateRoleAssignmentItem>, addAnother?: boolean) => void;
  isPartnerOrg: boolean;
  groupType?: GroupType;
  availableRoleNames?: string[];
}> = (props) => {
  const { t } = useTranslation();
  const { onClose, groupId, groupOrganization } = props;
  const [form] = Form.useForm<GroupRoleAssignmentFormData>();

  const newRoleAssignmentStore = container.get(RoleAssignmentItemStore);
  const newRoleAssignmentState = newRoleAssignmentStore.state;

  const [selectedOrg, setSelectedOrg] = useState<string | undefined>(props.groupType === GroupType.License ? groupOrganization : undefined);

  const organizationSelector = props.isPartnerOrg ? Selector.Partners : Selector.Organization;
  const [currentMaxSelector, setCurrentMaxSelector] = useState(organizationSelector);
  const [sptAbIsSelected, setIsSptAbSelected] = useState(false);

  const roleAssignmentSummaryStore = container.get(RoleAssignmentSummaryStore);
  const roleAssignmentSummaryState = roleAssignmentSummaryStore.state;
  const [roleAttributes, setRoleAttributes] = useState<RoleAssignmentAttributes>();
  const [customError, setCustomError] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [saveRoleWarning, setSaveRoleWarning] = useState(false);
  const [addAnother, setAddAnother] = useState(false);
  const rolesStore = container.get(RoleSummaryStore);
  const rolesState = rolesStore.state;
  const [expiredRolesWarningMessage, setExpiredRolesWarningMessage] = useState('');
  const expiredRoleAssignments = useRef<{ roleId: string; scopeId: string; assignmentId: string; label: string }[]>([]);
  const { modal } = App.useApp();

  const licenseCreationAttributesStore = useMemo(() => container.get(LicenseCreationAttributesStore), []);
  const unlimitedLicenseDefaults = getUnlimitedLicenseAttributes();
  const licenseCreationAttributesStoreState = licenseCreationAttributesStore.state;
  const [licenseAttributesVisible, setLicenseAttributesVisible] = useState<boolean>(false);
  const [licenseAttributes, setLicenseAttributes] = useState<LicenseManagerAssignmentAttributes>();

  const getWarningMessage = async () => {
    let item = (await form.validateFields()) as GroupRoleAssignmentFormData;
    if (item) {
      const scopeId = props.groupType === GroupType.License ? selectedOrg : item.hierarchy.scopeId;

      var query = {
        filter: {
          identityId: { value: groupId, type: 'guid' },
          identityType: IdentityType.Group,
          scopeId: { value: scopeId, type: 'guid' },
        },
        skip: 0,
      } as Query;
      var selectedRoles = item.roles.map((x) => {
        return { value: x.value, label: x.label };
      });

      await roleAssignmentSummaryStore.load(query);
      var existingRoles = verifyIfGroupAlreadyHasSelectedRole(
        roleAssignmentSummaryState.items.value
          .filter((x) => x.scopeId === scopeId)
          .map((x) => ({ id: x.roleId, expirationDate: x.expirationDate, scopeId: x.scopeId, assignmentId: x.id })),
        selectedRoles
      );

      const now = moment(new Date());

      if (existingRoles.length > 0 && existingRoles.some((role) => moment(role.expirationDate).isAfter(now))) {
        modal.error({
          content: t('SelectedUserAlreadyHasTheSelectedRoles').replace(
            '{0}',
            existingRoles
              .filter((v) => moment(v.expirationDate).isAfter(now))
              .map((v) => v.label)
              .join(', ')
          ),
        });
        return;
      } else {
        var roles = item.roles.filter((v) => !existingRoles.some((x) => x.id === v.value)).map((x) => x.label);
        var rolesString = roles.join(', ');

        setWarningMessage(
          roles.length > 0
            ? t('AssignRoleWarning').replace('{0}', rolesString).replaceAll('{1}', props.groupName).replace('{2}', props.organizationName)
            : ''
        );
        const expiredRoles = existingRoles.filter((v) => moment(v.expirationDate).isBefore(now));
        expiredRoleAssignments.current = expiredRoles.map((v) => ({
          roleId: v.id,
          scopeId: v.scopeId,
          assignmentId: v.assignmentId,
          label: v.label,
        }));
        const expiredRoleString = expiredRoles.map((v) => v.label).join(', ');
        setExpiredRolesWarningMessage(
          expiredRoles.length > 0 ? t('UpdateExpiredRoleWarning').replace('{0}', expiredRoleString).replaceAll('{1}', props.groupName) : ''
        );
      }
    }
    setSaveRoleWarning(true);
  };

  const onRoleAttributesChange = (item: RoleAssignmentAttributes) => {
    setRoleAttributes(item);
  };

  const onNewItem = async () => {
    let item = (await form.validateFields()) as GroupRoleAssignmentFormData;
    const scopeId = props.groupType === GroupType.License ? selectedOrg : item.hierarchy.scopeId;

    const newRoles = item.roles.filter((v) => !expiredRoleAssignments.current.some((x) => x.roleId === v.value));

    let isSuccess = true;
    if (newRoles.length > 0) {
      const postData: CreateRoleAssignmentItem = {
        identityId: groupId,
        identityType: IdentityType.Group,
        roles: newRoles.map((r) => ({ roleId: r.value, role: r.label, expirationDate: roleAttributes?.expirationDate })),
        scopeId: scopeId ?? item.hierarchy.scopeId,
      };

      isSuccess = isSuccess && (await newRoleAssignmentStore.create(postData))?.isSuccess === true;

      if (isSuccess && !expiredRoleAssignments.current.some((v) => v.label === LicenseManagerRoleName)) {
        await createLicenseAttributes(groupId, postData.scopeId);
      }
    }
    if (expiredRoleAssignments.current.length > 0 && roleAttributes?.expirationDate) {
      const response = await roleAssignmentSummaryStore.updateExpirationInBulk(
        expiredRoleAssignments.current.map((v) => ({
          assignmentId: v.assignmentId,
          scopeId: v.scopeId,
          newExpirationDate: roleAttributes.expirationDate!,
        }))
      );
      isSuccess = isSuccess && response?.isSuccess === true;
      const existingLicenseManagerRole = expiredRoleAssignments.current.find((v) => v.label === LicenseManagerRoleName);
      if (isSuccess && existingLicenseManagerRole) {
        await createLicenseAttributes(groupId, existingLicenseManagerRole.scopeId);
      }
    }
    expiredRoleAssignments.current = [];
    form.resetFields();
    handleClose({ isSuccess });
  };

  const createLicenseAttributes = async (groupId: string, scopeId: string) => {
    if (licenseAttributes) {
      var item: NewLicensCreationAttributes = {
        identityType: IdentityType.Group,
        maximumLicenseDueDate: licenseAttributes.maximumLicenseDueDate,
        maxLicensesPeriodInDays: licenseAttributes.maxLicensesPeriodInDays,
        maxNumberOfLicensesPerMonth: licenseAttributes.maxNumberOfLicensesPerMonth,
        allowedDevices: licenseAttributes.allowedDevices,
        identityId: groupId,
        scopeId,
        needsSignOff: licenseAttributes.needsSignOff,
        canCreateUnlimitedLicense: licenseAttributes.canCreateUnlimitedLicense,
        canCreateRecurrentLicenses: licenseAttributes.canCreateRecurrentLicenses ?? false,
      };
      if (item.canCreateUnlimitedLicense) {
        item.maximumLicenseDueDate = unlimitedLicenseDefaults.untilDate;
        item.maxLicensesPeriodInDays = unlimitedLicenseDefaults.maxPeriodInDays;
      }
      await licenseCreationAttributesStore.post(item);
    }
  };
  const onRolesChange = async (values: { value: string; label: string; selector: Selector }[]) => {
    var licenseMnagerRoleSelected = values.any((x) => x.label === LicenseManagerRoleName);
    if (!licenseAttributesVisible) {
      licenseMnagerRoleSelected && setLicenseAttributesVisible(true);
    } else {
      !licenseMnagerRoleSelected && setLicenseAttributesVisible(false);
    }
  };
  const onLicenseAttributesChange = (item: LicenseManagerAssignmentAttributes) => {
    setLicenseAttributes(item);
  };

  const verifyIfGroupAlreadyHasSelectedRole = (
    groupRoles: { id: string; expirationDate?: Date; scopeId: string; assignmentId: string }[],
    selectedRoles: { value: string; label: string }[]
  ) => {
    var existingRoles = [];
    for (const selectedRole of selectedRoles) {
      const groupRole = groupRoles.find((v) => v.id === selectedRole.value);
      if (groupRole) {
        existingRoles.push({
          id: groupRole.id,
          expirationDate: groupRole.expirationDate,
          label: selectedRole.label,
          scopeId: groupRole.scopeId,
          assignmentId: groupRole.assignmentId,
        });
      }
    }
    return existingRoles;
  };

  const handleClose = (response?: any) => {
    newRoleAssignmentStore.clearMessages();
    roleAssignmentSummaryStore.clearMessages();
    licenseCreationAttributesStore.clearMessages();
    onClose(response, addAnother);
  };

  return (
    <NewItemModal
      width={600}
      className="figma-modal"
      title={t('AssignRolesToGroup')}
      onNewItem={getWarningMessage}
      onClose={handleClose}
      onOkButtonIsDisabled={newRoleAssignmentState.isBusy.get()}
      onCancelButtonIsDisabled={newRoleAssignmentState.isBusy.get()}
      closable={!newRoleAssignmentState.isBusy.get()}
      maskClosable={!newRoleAssignmentState.isBusy.get()}
      okText={t('Confirm')}
      errorMessage={newRoleAssignmentState.errorMessage}
      body={
        <>
          {(customError || '').length !== 0 && <Alert type="error" message={customError} closable onClose={() => setCustomError('')} />}
          <Spin spinning={newRoleAssignmentState.isBusy.get() || rolesState.isBusy.get()}>
            <Form size="middle" form={form} layout="vertical" autoComplete="off" initialValues={{ roles: [] }}>
              {props.groupType === GroupType.Default && (
                <Form.Item
                  name={nameof<GroupRoleAssignmentFormData>('hierarchy')}
                  rules={[
                    () => ({
                      required: true,
                      validator(_, value) {
                        if ((value?.scopeId || '').length === 0) return Promise.reject(t('PleaseSelect'));
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <ScopeHierarchySelector
                    allowedScopeRoots={groupOrganization ? [groupOrganization] : []}
                    onChange={(x) => {
                      const selector = getSelectorFromScope(x.lastScope ?? ScopeType.Organization);
                      if (selector === Selector.Organization) {
                        if (x.scopeTitle.toLowerCase() === 'sptab') {
                          setIsSptAbSelected(true);
                        } else {
                          setIsSptAbSelected(false);
                        }
                        setCurrentMaxSelector(organizationSelector);
                      } else {
                        setCurrentMaxSelector(selector);
                      }
                      form.setFieldsValue({ ...form.getFieldsValue(), roles: [] });
                      setSelectedOrg((x?.path || []).length < 2 ? undefined : x.path[1]);
                    }}
                  />
                </Form.Item>
              )}
              <Form.Item name={nameof<GroupRoleAssignmentFormData>('roles')} rules={[{ required: true, message: t('PleaseSelect') }]}>
                <RolesSelect
                  onChange={onRolesChange}
                  organizationId={selectedOrg}
                  filterNames={props.groupType === GroupType.License ? props.availableRoleNames : undefined}
                  maxSelector={currentMaxSelector}
                  excludeNames={
                    props.groupType === GroupType.License
                      ? undefined
                      : [
                          LicenseManagerRoleName,
                          LicenseApproverRoleName,
                          StructmasterUserRoleName,
                          !sptAbIsSelected ? GlobalLicenseManagerRoleName : '',
                        ]
                  }
                />
              </Form.Item>
              <Text style={{ marginBottom: 10 }} strong>
                {t('RoleAssignmentAttributes')}:
              </Text>
              <RoleAssignmentAttributesComponent onChange={onRoleAttributesChange} />

              {licenseAttributesVisible && (
                <>
                  <Divider />
                  <Text style={{ marginBottom: 10 }} strong>
                    {t('LicenseManagerRoleAttributes')}:
                  </Text>
                  <LicenseAttributesComponent form={form} onChange={onLicenseAttributesChange} />
                </>
              )}
            </Form>

            {props.availableRoleNames && props.availableRoleNames.length > 1 && (
              <Checkbox value={addAnother} onChange={(v) => setAddAnother(v.target.checked)} style={{ marginBottom: 20 }}>
                {t('AddAnotherAssignment')}
              </Checkbox>
            )}
          </Spin>
          {saveRoleWarning && (warningMessage || expiredRolesWarningMessage) && (
            <Modal
              title={<h3>{`${t('Confirmation')}`}</h3>}
              open={saveRoleWarning}
              onOk={() => {
                setSaveRoleWarning(false);
                onNewItem();
              }}
              onCancel={() => handleClose()}
              okText={t('OK')}
              okButtonProps={{
                style: { width: '45%', float: 'right' },
                disabled: licenseCreationAttributesStoreState.isBusy.get() || newRoleAssignmentState.isBusy.get(),
              }}
              cancelText={t('Cancel')}
              cancelButtonProps={{
                type: 'default',
                style: { width: '45%' },
                disabled: licenseCreationAttributesStoreState.isBusy.get() || newRoleAssignmentState.isBusy.get(),
              }}
            >
              <Spin spinning={newRoleAssignmentState.isBusy.get() || licenseCreationAttributesStoreState.isBusy.get()}>
                {warningMessage !== '' && <p>{warningMessage}</p>}
                {expiredRolesWarningMessage !== '' && <p>{expiredRolesWarningMessage}</p>}
                <p>{t('DoYouWantToContinue')}</p>
              </Spin>
            </Modal>
          )}
        </>
      }
    />
  );
};

export default GroupRoleAssignmentModal;
